// @flow

import React from 'react'
import { useEffect } from 'react'
import { graphql } from 'gatsby'
import { css } from '@emotion/core'
import Layout from '@/layouts'
import Feature from '@/components/Feature'
import PhotoCollage from '@/components/PhotoCollage'
import MediaBlock from '@/components/MediaBlock'
import Locations from '@/components/Locations'
import PhotoFeature from '@/components/PhotoFeature'
import Options from '@/components/Options'

const OptionsStyles = css`
  background: var(--c-veryLightPink);
  padding: 100px 0;
`

type Props = {
  data: Object,
}

function ResidenciesPage({
  data: {
    page: {
      data: {
        title,
        metaTitle,
        heroTitle,
        heroDescription: { html: heroDescription },
        heroVideo,
        heroPhoto,
        collage,
        residences,
        careTitle,
        careDescription: { html: careDescription },
        careItems,
        photoFeatureTitle,
        photoFeatureSubtitle,
        photoFeatureDescription: { html: photoFeatureDescription },
        photoFeaturePhoto,
      },
    },
    settings
  },
}: Props) {

  useEffect(() => {
    document.title = 'Arcadia.org' + (title ? " | " + title : "")
  }, []);

  return (
    <Layout
        logoSrc={settings.data.company_logo.url}
        logoAlt={settings.data.company_logo.alt}>
      <Feature
        wrapperBackground
        hero={true}
        title={heroTitle}
        titleAccent
        largeTitle
        description={
          <div dangerouslySetInnerHTML={{ __html: (heroDescription || "") + (heroVideo || "") }} />
        }
        mediaBlock={<MediaBlock src={heroPhoto.url} alt="" />}
      />
	  <PhotoCollage
	    styles={WorkingHereStyles}
		photos={collage.map(c => c.photo)}
	  />
      <Locations locations={residences} />
      <Options
        css={OptionsStyles}
        layout="detailed"
        subtitle="Key Feature"
        title={careTitle}
        description={
          <div dangerouslySetInnerHTML={{ __html: careDescription }} />
        }
        options={careItems.map(
          ({
            icon: { url: icon },
            description: { html: description },
            title,
          }) => ({
            icon: { src: icon },
            description,
            title,
          })
        )}
      />
      <PhotoFeature
        subtitle={photoFeatureSubtitle}
        title={photoFeatureTitle}
        description={
          <div dangerouslySetInnerHTML={{ __html: photoFeatureDescription }} />
        }
        img={{ src: photoFeaturePhoto.url, alt: '' }}
      />
    </Layout>
  )
}

export default ResidenciesPage

export const query = graphql`
  {
    page: prismicResidences {
      data {
        title
        heroTitle: hero_title
        heroPhoto: hero_photo {
          url
        }
        heroDescription: hero_description {
          html
        }
        heroVideo: hero_video
        collage {
          photo: collage_photo {
            url
          }
        }
        residencesTitle: locations_title
        residences: locations {
          location {
            uid
            type
            document {
              ... on PrismicResidence {
                uid
                type
                data {
                  title
                  description: preview_description {
                    html
                  }
                  ctaLabel: preview_cta_label
                  ctaLink: preview_cta_link {
                    uid
                    type
                  }
                  photo: preview_photo {
                    url
                  }
                }
              }
            }
          }
        }
        careTitle: care_title
        careDescription: care_description {
          html
        }
        careItems: care_items {
          icon: care_item_icon {
            url
          }
          title: care_item_title
          description: care_item_description {
            html
          }
        }
        photoFeatureTitle: photo_feature_title
        photoFeatureSubtitle: photo_feature_subtitle
        photoFeatureDescription: photo_feature_description {
          html
        }
        photoFeaturePhoto: photo_feature_photo {
          url
        }
      }
    }
    settings: prismicGlobalSettings {
      ...LogoSettings
    }
  }
`

const WorkingHereStyles = css`
  grid-template-columns: 1fr 1fr 2fr;
  img {
    padding-bottom: 30px;
    &:nth-of-type(1) {
      grid-area: 1 / 1 / 2 / 2;
    }
    &:nth-of-type(2) {
      grid-area: 1 / 2 / 2 / 3;
    }
    &:nth-of-type(3) {
      grid-area: 1 / 3 / 3 / 4;
    }
    &:nth-of-type(4) {
      grid-area: 2 / 1 / 3 / 3;
    }
  }
`
